/**
 * GameSessionInfo - InfoActor subclass for replicating game session state.
 * Replaces custom SESSION_STATE protocol messages with standard actor replication.
 */

import { GameSessionState } from '../multiplayer/interface.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { InfoActor } from './InfoActor.js';

import type { PlayerInfo } from './PlayerInfo.js';


@EngineClass('GameSessionInfo')
export class GameSessionInfo extends InfoActor {
  /** Current session state (Initializing, WaitingForPlayers, Starting, InProgress, Ending, Shutdown) */
  @property({ replicate: true })
  public sessionState: GameSessionState = GameSessionState.Initializing;

  /** Current number of connected players */
  @property({ replicate: true, net: { netType: 'uint' } })
  public playerCount: number = 0;

  /** Maximum number of players allowed in this session */
  @property({ replicate: true, net: { netType: 'uint' } })
  public maxPlayers: number = 16;

  /** Countdown timer (e.g., seconds until match starts), undefined if not counting down */
  @property({ replicate: true, net: { netType: 'float', quantization: { mode: 'scale', scale: 10 } } })
  public countdown: number | undefined = undefined;

  /**
   * Server-only: Maps `clientId` → `PlayerInfo` for every player currently in the session.
   * Populated when a `PlayerInfo` is linked to a spawned `PlayerController`; entries are
   * removed when the player disconnects. Not replicated — server use only.
   */
  public readonly activePlayers: Map<string, PlayerInfo> = new Map();

  public override initialize(options?: Record<string, unknown>): void {
    super.initialize(options);

    // Enable replication for this actor
    this.replicated = true;
  }

  public onRepSessionState(): void {
    this.onSessionStateChanged(this.sessionState, this.sessionState);
  }

  /**
   * Called on clients when session state changes. Override to trigger UI updates.
   * @param oldState - Previous session state
   * @param newState - New session state
   */
  protected onSessionStateChanged(oldState: GameSessionState, newState: GameSessionState): void {
    console.log(`[GameSessionInfo] Session state changed: ${oldState} → ${newState}`);

    // Example: Trigger UI updates, play sounds, etc.
    // This is where you'd hook into your game's UI system
  }
}

